import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import {
  apiGetEmployeeLeaveBalance,
  apiGetLeaveCompanyPolicy,
  apiGetLeaveRequests,
} from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { CalendarDays, Clock } from "lucide-react";
import type { EmployeeProfile } from "@/data/employees";
import { AnnualLeaveBalanceSummary } from "@/components/leave/AnnualLeaveBalanceSummary";
import { useDateLocale } from "@/hooks/useDateLocale";
import {
  formatLeaveDateRange,
  getLeaveStatusLabel,
  getLeaveTypeLabel,
} from "@/lib/leave-i18n";

interface Props {
  employee: EmployeeProfile;
}

const statusColors: Record<string, string> = {
  Pending: "bg-amber-500/10 text-amber-600 border-amber-500/20",
  "Pending Employee Document": "bg-amber-500/10 text-amber-600 border-amber-500/20",
  "Document Uploaded": "bg-blue-500/10 text-blue-600 border-blue-500/20",
  "HR Review Required": "bg-blue-500/10 text-blue-600 border-blue-500/20",
  "In Review": "bg-blue-500/10 text-blue-600 border-blue-500/20",
  Approved: "bg-emerald-500/10 text-emerald-600 border-emerald-500/20",
  Rejected: "bg-red-500/10 text-red-600 border-red-500/20",
  Cancelled: "bg-gray-500/10 text-gray-600 border-gray-500/20",
};

export function ProfileTimeOffTab({ employee }: Props) {
  const { t } = useTranslation();
  const dateLocale = useDateLocale();
  const employeeId = Number(employee.id);
  const currentYear = new Date().getFullYear();

  const { data: requests = [] } = useQuery({
    queryKey: ["employee-leave-requests", employee.id],
    queryFn: () => apiGetLeaveRequests({ employeeId }),
    enabled: Number.isFinite(employeeId),
  });

  const { data: balances = [], isPending: balanceLoading, isError: balanceError, refetch: refetchBalance } = useQuery({
    queryKey: ["employee-leave-balance", employee.id, currentYear],
    queryFn: () => apiGetEmployeeLeaveBalance(employeeId, currentYear),
    enabled: Number.isFinite(employeeId),
  });

  const { data: companyPolicy, isPending: policyLoading, isError: policyError, refetch: refetchPolicy } = useQuery({
    queryKey: ["leaveCompanyPolicy"],
    queryFn: apiGetLeaveCompanyPolicy,
  });

  const annualBalance = balances.find((balance) => balance.leaveType === "annual");

  return (
    <div className="space-y-4 max-w-3xl">
      <AnnualLeaveBalanceSummary balance={annualBalance} policy={companyPolicy} loading={balanceLoading || policyLoading} error={balanceError || policyError} onRetry={() => { void refetchBalance(); void refetchPolicy(); }} />

      {/* Requests */}
      <Card className="shadow-card">
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-semibold flex items-center gap-2">
            <CalendarDays className="h-4 w-4 text-primary" />
            {t("profile.timeOff.requests")}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {requests.length === 0 ? (
            <p className="text-sm text-muted-foreground py-4 text-center italic">{t("profile.timeOff.noRequests")}</p>
          ) : (
            <div className="divide-y divide-border">
              {requests.map((req) => (
                <div key={req.id} className="py-3 flex items-start justify-between gap-4">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-sm font-medium">
                        {getLeaveTypeLabel(t, req.leaveType, req.leaveTypeLabel)}
                      </span>
                      <Badge variant="outline" className={`text-[10px] px-1.5 py-0 ${statusColors[req.status] || ""}`}>
                        {getLeaveStatusLabel(t, req.status)}
                      </Badge>
                    </div>
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      <Clock className="h-3 w-3" />
                      {formatLeaveDateRange(req.startDate, req.endDate, dateLocale)}
                    </div>
                    {req.notes && (
                      <p className="text-xs text-muted-foreground mt-1">{req.notes}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
